import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, BookOpen, Phone, ShoppingBasket, GraduationCap } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useBasket } from "@/context/BasketContext";
import { SchoolOrdersModal } from "@/components/storefront/SchoolOrdersModal";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { settings } = useStore();
  const { count } = useBasket();
  const navigate = useNavigate();
  const [schoolOpen, setSchoolOpen] = useState(false);

  return (
    <header
      className="sticky top-0 z-50 bg-brand-blue border-b-2 border-black"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <span className="w-10 h-10 flex items-center justify-center bg-brand-gold border-2 border-black rounded-md shadow-brutal-black group-hover:-translate-y-0.5 transition-transform duration-200">
            <BookOpen className="w-5 h-5 text-brand-blue" strokeWidth={2.5} />
          </span>
          <span className="font-display font-black text-xl md:text-2xl tracking-tight text-white leading-none">
            Books<span className="text-brand-gold">N</span>more
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-md font-medium text-sm transition-colors duration-200 ${
                  isActive ? "bg-brand-gold text-black" : "text-white hover:bg-white/10"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/basket"
            data-testid="header-basket-btn"
            className="relative inline-flex items-center justify-center w-11 h-11 bg-white text-brand-blue border-2 border-black rounded-md shadow-brutal-black hover:-translate-y-0.5 transition-all duration-200"
            aria-label="Shopping list"
          >
            <ShoppingBasket className="w-5 h-5" strokeWidth={2.5} />
            {count > 0 && (
              <span data-testid="basket-count" className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 flex items-center justify-center bg-brand-gold text-black text-xs font-black border-2 border-black rounded-full">
                {count}
              </span>
            )}
          </Link>
          <button
            onClick={() => setSchoolOpen(true)}
            data-testid="header-school-orders-btn"
            className="hidden sm:inline-flex items-center gap-2 bg-white text-brand-blue font-semibold text-sm px-4 py-2 border-2 border-black rounded-md shadow-brutal-black hover:-translate-y-0.5 transition-all duration-200"
          >
            <GraduationCap className="w-4 h-4" strokeWidth={2.5} /> School Orders
          </button>
          {settings?.phone && (
            <a
              href={`tel:${settings.phone}`}
              data-testid="header-call-btn"
              className="hidden sm:inline-flex items-center gap-2 bg-brand-gold text-black font-semibold text-sm px-4 py-2 border-2 border-black rounded-md shadow-brutal-black hover:-translate-y-0.5 transition-all duration-200"
            >
              <Phone className="w-4 h-4" strokeWidth={2.5} /> Call to order
            </a>
          )}
          <button
            className="md:hidden p-2 border-2 border-white rounded-md text-white"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t-2 border-black bg-brand-blue px-4 py-3 space-y-1" data-testid="mobile-menu">
          {links.map((l) => (
            <button
              key={l.to}
              onClick={() => {
                navigate(l.to);
                setOpen(false);
              }}
              className="block w-full text-left px-4 py-3 rounded-md font-medium text-white hover:bg-white/10"
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => { setSchoolOpen(true); setOpen(false); }}
            className="block w-full text-left px-4 py-3 rounded-md font-medium text-white hover:bg-white/10"
            data-testid="mobile-school-orders-btn"
          >
            School Orders
          </button>
        </div>
      )}
      <SchoolOrdersModal open={schoolOpen} onOpenChange={setSchoolOpen} />
    </header>
  );
};
